import { InvoiceStatus } from "@/data/enums/invoice";
import {
  IInvoice,
  IInvoiceProduct,
  InvoiceCategoryCode
} from "@/models/invoices";
import { Component, Vue } from "vue-property-decorator";
import _ from "@/boot/lodash";
import { dataLayer } from "@/boot/gtm";
import { QUERY_PARAMS } from "@/data/constants";

const OrderCategoryCodes = [
  InvoiceCategoryCode.NEW_CONTRACT,
  InvoiceCategoryCode.ONE_TIME_SERVICE
];

@Component
export default class PurchaseEventMixin extends Vue {
  // Methods
  public checkPurchaseEvent(invoice: IInvoice) {
    // Abort if no invoice
    if (_.isEmpty(invoice)) return;

    try {
      // Get attempt
      const attempt = this.$route.query[QUERY_PARAMS.ATTEMPT] as string;
      // Abort if no payment attempt
      if (!attempt) throw "no_payment_attempt";
      // Decode and parse attempt data
      const attemptData = JSON.parse(atob(attempt));
      // Abort if attempt is for a different invoice
      if (`${_.get(attemptData, "invoiceId")}` !== invoice.id)
        throw "different_invoice";
      // Abort if invoice is not 'PAID'
      if (_.get(invoice, "status.code") !== InvoiceStatus.PAID)
        throw "invoice_not_paid";
      // Abort if invoice is not for a new order
      if (!OrderCategoryCodes.includes(invoice.category.slug))
        throw "non_order_invoice";
      // Attempt moment
      const attemptMoment = this.$moment.utc(attemptData.timestamp);
      // Paid moment
      const paidMoment = this.$moment.utc(invoice.paid_datetime);
      // Abort if paid date is BEFORE attempt date
      if (paidMoment.isBefore(attemptMoment)) throw "paid_before_attempt";
      // Push dataLayer purchase event
      this.pushPurchaseEvent(invoice);
    } catch {
      // Fail silently
    } finally {
      // Clear 'attempt' query param
      this.clearAttemptQueryParam();
    }
  }

  public pushPurchaseEvent(invoice: IInvoice) {
    if (_.isEmpty(invoice)) return;
    dataLayer("event", "purchase", {
      currency: invoice.currency.code,
      tax: invoice.tax_amount,
      value: invoice.total_amount_converted,
      transaction_id: invoice.number,
      items: _.map(invoice.products, (product: IInvoiceProduct) => ({
        id: product.product.id,
        name: product.product.name,
        category: product.product.category.name,
        quantity: product.quantity
        /* Commenting out until BE can return unformatted values */
        // tax: product.tax_amount,
        // price: product.total_amount_converted,
        // discount: product.total_discount_amount
      }))
    });
  }

  public clearAttemptQueryParam() {
    const query = _.clone(this.$route.query);
    const regex = new RegExp(`^(${[QUERY_PARAMS.ATTEMPT].join("|")})$`, "gm");
    const matches = _.keys(query)
      .join("\n")
      .match(regex);
    if (!matches || !matches.length) return;
    matches.forEach(key => delete query[key]);
    this.$router.replace({ query });
  }
}
